import { Box, Button, Checkbox, Stack, SxProps, Theme } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";

import { IFlowStepProps } from "../../../Flows/types/IFlow";
import { isMobileView } from "../../../types/Constants";
import { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import StepWrapper from "../../Components/StepWrapper";
import IMedicalConclusions from "../types/IMedicalConclusions";
import IMedicalQualification from "../types/IMedicalQualification";
import { IMedicalStepConfig, IMedicalStepInnerContentProps, IMedicalStepProps } from "./FormSeriesMedicalStep";

export const classNames = {
  noToAllButton: "medicalStep-noToAllButton"
};

const scrollWindowToBottom = () => {
  const scrollingElement = document.scrollingElement ?? document.documentElement;
  const targetTop = Math.max(scrollingElement.scrollHeight, document.body?.scrollHeight ?? 0);
  window.scrollTo({ top: targetTop, behavior: "smooth" });
};

export interface INoToAllFormSeriesMedicalStepDecoratorConfig {
  /** Renders the inner medical step with no-to-all behavior applied to the configured questions. */
  InnerForm: React.FC<IMedicalStepInnerContentProps>;
  /** The medical question ids to reset to false for a no-to-all action. */
  rootMedicalQuestionIds: string[];
  /** The nested/conditional medical question ids to reset to null for a no-to-all action. */
  nestedMedicalQuestionIds: string[];
}

const resetNoToAllMedicalFormState = (
  oldState: IMedicalQualification,
  rootMedicalQuestionIds: string[],
  nestedMedicalQuestionIds: string[]
): IMedicalQualification => {
  const resetFormState = { ...oldState };
  rootMedicalQuestionIds.forEach(questionId => {
    resetFormState[questionId] = false;
  });
  nestedMedicalQuestionIds.forEach(questionId => {
    resetFormState[questionId] = null;
  });
  return resetFormState;
};

const resetNoToAllMedicalConclusions = (oldState: IMedicalConclusions): IMedicalConclusions => ({
  ...oldState,
  hasRadiationProtectedAreas: false,
  hasPacemakerWithoutDefibrillator: null,
  hasDiabetes: false
});

const resetToDefaultMedicalFormState = (oldState: IMedicalQualification): IMedicalQualification => {
  const resetFormState = { ...oldState };
  Object.keys(resetFormState).forEach(key => {
    resetFormState[key as keyof IMedicalQualification] = null;
  });
  return resetFormState;
};

const resetToDefaultMedicalConclusions = (oldState: IMedicalConclusions): IMedicalConclusions => {
  const resetConclusions: Record<string, null> = {};
  Object.keys(oldState).forEach(key => {
    resetConclusions[key] = null;
  });
  return resetConclusions as unknown as IMedicalConclusions;
};

interface INoToAllButtonProps {
  onClick: () => void;
  checked: boolean;
  sx?: SxProps<Theme>;
}

const NoToAllButton: React.FC<INoToAllButtonProps> = ({ onClick, checked, sx }) => {
  return (
    <Box alignItems="center" display="flex" justifyContent="flex-end" width="100%" sx={{ ...sx }}>
      <Button
        className={classNames.noToAllButton}
        type="button"
        onClick={onClick}
        sx={{
          color: "common.white",
          "& .MuiButton-endIcon": {
            color: "inherit"
          }
        }}
        endIcon={
          <Checkbox
            checked={checked}
            disableRipple
            sx={{
              p: 0,
              pointerEvents: "none",
              color: "inherit",
              "&.Mui-checked": { color: "inherit" }
            }}
            tabIndex={-1}
          />
        }
      >
        No to All
      </Button>
    </Box>
  );
};

/** Decorates the shared form-series medical step by injecting top/bottom No-to-All controls into the form question stream. */
const NoToAllFormSeriesMedicalStepDecorator: React.FC<
  IMedicalStepProps & IMedicalStepConfig & IStepWrapperButtonProps & INoToAllFormSeriesMedicalStepDecoratorConfig
> = ({
  InnerForm,
  FormQuestions,
  rootMedicalQuestionIds,
  nestedMedicalQuestionIds,
  medicalQualificationEvaluator,
  nextStep,
  router,
  onMedicalNoncandidateDisqualification,
  useCreateNonCandidateContactResult,
  NextButtonMobile,
  NextButtonDesktop,
  ...props
}) => {
  const showMobile = isMobileView();
  const flowStepProps = useOutletContext<IFlowStepProps>();
  const pendingNoToAllScrollRef = useRef(false);
  const { execute: createNonCandidateContactResult, result, isLoading } = useCreateNonCandidateContactResult();

  useEffect(() => {
    if (result) router.goToNonCandidateView();
  }, [result]);

  useEffect(() => {
    if (!pendingNoToAllScrollRef.current) return;

    requestAnimationFrame(() => {
      scrollWindowToBottom();
      pendingNoToAllScrollRef.current = false;
    });
  }, [props.value]);

  const handleNext = () => {
    const isNonCandidate = medicalQualificationEvaluator.isNonCandidate(props.value);
    if (isNonCandidate) {
      onMedicalNoncandidateDisqualification();
      createNonCandidateContactResult();
    } else flowStepProps.setStep(nextStep);
  };

  const onNoToAll = () => {
    props.setMedicalFormState(oldState =>
      resetNoToAllMedicalFormState(oldState, rootMedicalQuestionIds, nestedMedicalQuestionIds)
    );
    props.setMedicalConclusions(resetNoToAllMedicalConclusions);
  };

  const onNoToAllWithScroll = () => {
    pendingNoToAllScrollRef.current = true;
    onNoToAll();

    requestAnimationFrame(() => {
      scrollWindowToBottom();
    });
  };

  const onReset = () => {
    props.setMedicalFormState(oldState => resetToDefaultMedicalFormState(oldState));
    props.setMedicalConclusions(resetToDefaultMedicalConclusions);
  };

  const onResetWithScroll = () => {
    pendingNoToAllScrollRef.current = true;
    onReset();

    requestAnimationFrame(() => {
      scrollWindowToBottom();
    });
  };

  const isNoToAllChecked =
    rootMedicalQuestionIds.length > 0 && rootMedicalQuestionIds.every(questionId => props.value[questionId] === false);
  const mobileBottomSpacerHeightPx = 32;

  return (
    <StepWrapper
      NextButtonMobile={NextButtonMobile}
      NextButtonDesktop={NextButtonDesktop}
      nextButtonLoading={isLoading}
      onNext={handleNext}
      disabled={isLoading || !medicalQualificationEvaluator.isComplete(props.value)}
    >
      <Stack sx={{ pt: 2 }}>
        <Stack spacing={0} width="100%">
          <NoToAllButton
            checked={isNoToAllChecked}
            onClick={isNoToAllChecked ? onReset : onNoToAllWithScroll}
            sx={{ pb: 2 }}
          />
          <InnerForm
            value={props.value}
            setMedicalConclusions={props.setMedicalConclusions}
            setMedicalFormState={props.setMedicalFormState}
            FormQuestions={FormQuestions}
          />
          <NoToAllButton checked={isNoToAllChecked} onClick={isNoToAllChecked ? onReset : onNoToAll} />
          {showMobile ? <Box aria-hidden="true" height={`${mobileBottomSpacerHeightPx}px`} width="100%" /> : null}
        </Stack>
      </Stack>
    </StepWrapper>
  );
};

export default NoToAllFormSeriesMedicalStepDecorator;
